// Ninja's answers sometimes contain LaTeX with no math delimiters ("\text{NH}_4\text{Cl}") or with single
// "$" delimiters, which MathJax (default config: \( \), \[ \], $$ $$) leaves as raw text. These helpers
// wrap that LaTeX in \( \) so MathJax renders it, leaving the surrounding prose as plain text.

// One braced argument, allowing one level of nesting: {a}, {b^{2}}.
const BRACED = String.raw`\{(?:[^{}]|\{[^{}]*\})*\}`;
// A command with its arguments: \text{NH}, \frac{a}{b}, \rightarrow.
const COMMAND = String.raw`\\[a-zA-Z]+(?:${BRACED})*`;
// A sub/superscript: _4, ^{3+}, ^-.
const SCRIPT = String.raw`[_^](?:${BRACED}|[A-Za-z0-9+\-])`;
// A run starts with a command and keeps going through scripts, further commands and the numbers, signs,
// brackets and spaces between them: "\text{M}^{3+} + 3\text{OH}^- \rightarrow \text{M(OH)}_3\downarrow".
// Starting with a backslash command keeps ordinary prose like "file_name" untouched.
const BARE_LATEX_RUN = new RegExp(`${COMMAND}(?:[\\s0-9+\\-=()]*(?:${COMMAND}|${SCRIPT}))*`, "g");

// $...$ whose content looks like LaTeX (so "$5 and $10" stays as money).
const SINGLE_DOLLAR_MATH = /(^|[^\\$])\$(?!\$)([^$\n]*[\\^_{][^$\n]*)\$(?!\$)/g;

const HAS_DELIMITERS = /\\\(|\\\[|\$\$/;

export const wrapLatexInText = (text) => {
  if (typeof text !== "string" || !/[\\$]/.test(text)) return text;
  const wrapped = [];
  const keep = (math) => {
    wrapped.push(`\\(${math}\\)`);
    return `\u0000${wrapped.length - 1}\u0000`;
  };
  let result = text.replace(SINGLE_DOLLAR_MATH, (_m, before, math) => before + keep(math.trim()));
  // Text that already uses MathJax delimiters is left to MathJax.
  if (!HAS_DELIMITERS.test(result)) {
    result = result.replace(BARE_LATEX_RUN, (math) => keep(math));
  }
  return result.replace(/\u0000(\d+)\u0000/g, (_m, i) => wrapped[Number(i)]);
};

// Applies wrapLatexInText to every text node of an HTML string (outside code blocks).
export const wrapLatexInHtml = (html) => {
  if (typeof html !== "string" || !/[\\$]/.test(html) || typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach((node) => {
    if (node.parentElement?.closest("code, pre, script, style")) return;
    const wrapped = wrapLatexInText(node.nodeValue);
    if (wrapped !== node.nodeValue) node.nodeValue = wrapped;
  });
  return doc.body.innerHTML;
};
